// העלאת קבצים — the content writer's one screen, and the whole of what that role can do.
//
// It renders `GET /uploads/scope` and nothing else: retainer → its topics, plus any job
// granted on its own, by name. There is no search, no navigation and no other screen to
// reach, because there is nowhere else for this role to go.
//
// ⚠ THE LIST IS NOT A PERMISSION, AND THIS SCREEN NEVER TREATS IT AS ONE. It is what the
// server said, a moment ago, and the server re-reads the grant tables on every upload. A
// job whose grant is revoked between the page loading and the file being sent is refused
// — correctly — and the error appears here rather than the upload silently succeeding.
// Nothing in this file decides what may be uploaded; it only decides what to draw.
//
// ⚠ THERE IS NO DOWNLOAD, ANYWHERE ON THIS SCREEN. Not disabled, not hidden — the API has
// no route for it on this mount, so there is nothing to call. A writer sees the NAMES of
// what they sent, which is enough to know it arrived and not enough to read it back.
import { useState } from 'react';
import useAsync from '../../hooks/useAsync';
import he from '../../i18n/he';
import { uploadsApi } from '../../api';
import AsyncBoundary, { ErrorText } from '../../components/Loader';
import Button from '../../components/Button';
import DateText from '../../components/DateText';
import { TextInput } from '../../components/Form';

// One selectable job. Deliberately plain: a name and a client, which is all the writer is
// given and all they need to know which piece of work this is.
const JobButton = ({ job, selected, onSelect }) => (
  <button
    type="button"
    className={`nav-item uploads-job${selected ? ' active' : ''}`}
    onClick={() => onSelect(job)}
  >
    {job.title}
    {job.client_name && <span className="muted"> · {job.client_name}</span>}
  </button>
);

export default function Uploads() {
  const { data: scope, loading, error } = useAsync(() => uploadsApi.scope());
  const [job, setJob] = useState(null);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [mineKey, setMineKey] = useState(0);

  // Their own uploads for the selected job. Fetched only once a job is chosen, and
  // re-fetched after each send so the list is the server's answer rather than an
  // optimistic guess about what was stored.
  const mine = useAsync(
    () => (job ? uploadsApi.mine(job.id) : Promise.resolve([])),
    [job && job.id, mineKey],
  );

  const send = async () => {
    if (!file || !job) return;
    setBusy(true);
    setSendError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      if (title.trim()) form.append('title', title.trim());
      const created = await uploadsApi.upload(job.id, form);
      setNotice(he.limited.uploaded(created.original_name));
      setTitle('');
      setFile(null);
      setMineKey((k) => k + 1);
    } catch (e) {
      // Includes the case that matters: the grant was revoked while this page was open.
      // The server refused, and saying so is the honest outcome.
      setSendError(e);
    } finally {
      setBusy(false);
    }
  };

  const retainers = (scope && scope.retainers) || [];
  const standalone = (scope && scope.jobs) || [];
  const nothing = retainers.length === 0 && standalone.length === 0;

  return (
    <div className="uploads">
      <h1>{he.limited.title}</h1>

      <AsyncBoundary loading={loading} error={error}>
        {nothing ? (
          // Not an error — nobody has granted them anything yet. The wording says the
          // state rather than reporting a failure.
          <p className="uploads-empty">{he.limited.nothingGranted}</p>
        ) : (
          <div className="uploads-layout">
            {/* WHAT THEY MAY REACH — exactly the server's list, nested the way it was
                sent. A job granted on its own is shown standalone, because nesting it
                would name a retainer nobody granted them. */}
            <div className="uploads-jobs">
              <h3>{he.limited.pickJob}</h3>
              {retainers.map((r) => (
                <div key={r.id} className="uploads-group">
                  <div className="uploads-group-name">{r.title} · {r.client_name}</div>
                  {r.jobs.map((j) => (
                    <JobButton key={j.id} job={j} selected={job && job.id === j.id} onSelect={setJob} />
                  ))}
                </div>
              ))}
              {standalone.length > 0 && (
                <div className="uploads-group">
                  <div className="uploads-group-name">{he.limited.standalone}</div>
                  {standalone.map((j) => (
                    <JobButton key={j.id} job={j} selected={job && job.id === j.id} onSelect={setJob} />
                  ))}
                </div>
              )}
            </div>

            {job && (
              <div className="uploads-form">
                <h3>{job.title}</h3>

                <TextInput
                  label={he.limited.fileTitle}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />

                <div className="field">
                  <label>{he.limited.file}</label>
                  {/* The browser's accept list is a convenience only: the server decides
                      the type from the file's own bytes, so a renamed file is refused
                      there regardless of what this offers. */}
                  <input
                    type="file"
                    onChange={(e) => { setFile(e.target.files[0] || null); setNotice(null); }}
                  />
                </div>

                <Button onClick={send} disabled={!file || busy}>
                  {busy ? he.limited.uploading : he.limited.upload}
                </Button>

                {notice && <p className="muted small-note">{notice}</p>}
                {sendError && <ErrorText error={sendError} />}

                <h3 className="uploads-mine-head">{he.limited.mine}</h3>
                <AsyncBoundary loading={mine.loading} error={mine.error}>
                  {(mine.data || []).length === 0 ? (
                    <p className="muted">{he.limited.mineNone}</p>
                  ) : (
                    (mine.data || []).map((f) => (
                      // A name and a date. No link, no view, no download — there is no
                      // endpoint behind one.
                      <div key={f.id} className="uploads-file">
                        {f.title || f.original_name}
                        <span className="muted">
                          {' · '}<DateText value={f.uploaded_at} />
                          {' · '}{he.limited.sizeKb(Math.max(1, Math.round(f.size_bytes / 1024)))}
                        </span>
                      </div>
                    ))
                  )}
                </AsyncBoundary>
              </div>
            )}
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}
